import { CreditCardIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { ChequeDetails } from '@/components/postgrid/cheque-details/ChequeDetails';
import { LetterDetails } from '@/components/postgrid/postgrid-letters/letter-details/LetterDetails';

interface SentLetter {
  id: string;
  subject?: string | null;
  status?: string;
  created_at?: string | null;
}

interface SentCheque {
  id: string;
  amount: string;
  memo?: string | null;
  status?: string;
  created_at?: string | null;
}

interface SentMailIndicatorsProps {
  letters?: SentLetter[];
  cheques?: SentCheque[];
}

export const SentMailIndicators: React.FC<SentMailIndicatorsProps> = ({
  letters = [],
  cheques = [],
}) => {
  const [selectedLetterId, setSelectedLetterId] = useState<string | null>(null);
  const [selectedChequeId, setSelectedChequeId] = useState<string | null>(null);

  const hasLetters = letters && letters.length > 0;
  const hasCheques = cheques && cheques.length > 0;

  if (!hasLetters && !hasCheques) {
    return null;
  }

  const handleLetterClick = (e: React.MouseEvent, letterId: string) => {
    e.stopPropagation();
    setSelectedLetterId(letterId);
  };

  const handleChequeClick = (e: React.MouseEvent, chequeId: string) => {
    e.stopPropagation();
    setSelectedChequeId(chequeId);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Letters indicator */}
        {hasLetters && (
          <div className="flex items-center gap-1">
            {letters.map((letter) => (
              <button
                key={letter.id}
                type="button"
                onClick={(e) => handleLetterClick(e, letter.id)}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                title={`Letter sent ${letter.created_at ? new Date(letter.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}`}
              >
                <EnvelopeIcon className="h-3.5 w-3.5" />
                <span>Sent letter {formatDate(letter.created_at)}</span>
              </button>
            ))}
          </div>
        )}

        {/* Cheques indicator */}
        {hasCheques && (
          <div className="flex items-center gap-1">
            {cheques.map((cheque) => (
              <button
                key={cheque.id}
                type="button"
                onClick={(e) => handleChequeClick(e, cheque.id)}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                title={`Check for $${cheque.amount} sent ${cheque.created_at ? new Date(cheque.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}`}
              >
                <CreditCardIcon className="h-3.5 w-3.5" />
                <span>
                  Sent ${cheque.amount} {formatDate(cheque.created_at)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Drawers */}
      {selectedLetterId && (
        <LetterDetails
          letterId={selectedLetterId}
          open={!!selectedLetterId}
          onOpenChange={(open) => !open && setSelectedLetterId(null)}
        />
      )}

      {selectedChequeId && (
        <ChequeDetails
          chequeId={selectedChequeId}
          open={!!selectedChequeId}
          onOpenChange={(open) => !open && setSelectedChequeId(null)}
        />
      )}
    </>
  );
};
